// AdminLadderManagePage — Phase L-ADMIN1.
//
// Route: /admin/ladders/:slug/manage
//
// Primary admin surface for managing a ladder (Singles or Ladies Singles) as
// an always-open event. Provides:
//   • Manual re-ordering: drag-and-drop, move up/down/top/bottom, direct rank edit
//   • Atomic Save (POST /admin/ladders/:slug/reorder) with a before/after preview
//   • Cancel (discard staged changes)
//   • Add player: pick from approved profiles not yet on the ladder + choose rank
//   • Remove player: soft-remove with confirmation, warns about open challenges
//
// Backend contract (admin-only):
//   GET  /api/admin/ladders/:slug/roster    → { ladder, players[] }
//   POST /api/admin/ladders/:slug/reorder   { player_ids: [...] }
//   GET  /api/admin/profiles-without-player?ladder=:slug
//   POST /api/admin/players/add-to-ladder   { profile_id, ladder }
//   PUT  /api/admin/players/:id/rank        { new_rank }
//   DELETE /api/admin/players/:id
//
// Normal player-facing challenge movement rules DO NOT run during a manual
// reorder — the backend rewrites ranks directly and records changes to
// rank_history for audit.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowUp, ArrowDown, ArrowLineUp, ArrowLineDown, Trash, Plus,
  FloppyDisk, X, DotsSixVertical, Warning, MagnifyingGlass, CheckCircle,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../lib/api';
import ConfirmDialog from '../components/ConfirmDialog';
import EventMediaCard from '../components/events/EventMediaCard';

const FRIENDLY_NAME = {
  'singles-ladder': "Men's Singles Ladder",
  'ladies-singles-ladder': "Ladies Singles Ladder",
};

export default function AdminLadderManagePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ladder, setLadder]   = useState(null);       // { id, slug, name }
  const [players, setPlayers] = useState([]);         // authoritative server state
  const [staged, setStaged]   = useState([]);         // pending reorder buffer
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dragId, setDragId]   = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getLadderRoster(slug);
      setLadder(r.data?.ladder || null);
      setPlayers(r.data?.players || []);
      setStaged(r.data?.players || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ladder');
      navigate('/admin/events');
    } finally { setLoading(false); }
  }, [slug, navigate]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (players.length !== staged.length) return true;
    for (let i = 0; i < players.length; i++) {
      if (players[i].player_id !== staged[i].player_id) return true;
    }
    return false;
  }, [players, staged]);

  const movedFrom = useMemo(() => {
    const before = new Map(players.map((p, i) => [p.player_id, i + 1]));
    return staged.map((p, i) => ({
      ...p,
      new_rank: i + 1,
      old_rank: before.get(p.player_id) || null,
      moved: before.get(p.player_id) !== i + 1,
    }));
  }, [players, staged]);

  if (!user) return <div className="p-6 text-sm text-[#6a766e]">Loading…</div>;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  const move = (idx, delta) => {
    const dst = idx + delta;
    if (dst < 0 || dst >= staged.length) return;
    const next = staged.slice();
    [next[idx], next[dst]] = [next[dst], next[idx]];
    setStaged(next);
  };
  const moveTop = (idx) => {
    if (idx === 0) return;
    const next = staged.slice();
    const [p] = next.splice(idx, 1);
    next.unshift(p);
    setStaged(next);
  };
  const moveBottom = (idx) => {
    if (idx === staged.length - 1) return;
    const next = staged.slice();
    const [p] = next.splice(idx, 1);
    next.push(p);
    setStaged(next);
  };
  const editRank = (idx, rawValue) => {
    const target = Math.max(1, Math.min(staged.length, parseInt(rawValue, 10) || 1));
    if (target === idx + 1) return;
    const next = staged.slice();
    const [p] = next.splice(idx, 1);
    next.splice(target - 1, 0, p);
    setStaged(next);
  };
  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetIdx) => {
    if (!dragId) return;
    const fromIdx = staged.findIndex((p) => p.player_id === dragId);
    if (fromIdx < 0 || fromIdx === targetIdx) { setDragId(null); return; }
    const next = staged.slice();
    const [p] = next.splice(fromIdx, 1);
    next.splice(targetIdx, 0, p);
    setStaged(next);
    setDragId(null);
  };

  const discard = () => setStaged(players);

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await adminAPI.reorderLadder(slug, staged.map((p) => p.player_id));
      toast.success('Ladder order saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const removePlayer = (p) => {
    setConfirm({
      title: `Remove ${p.name} from ${ladder?.name || 'ladder'}?`,
      message: p.open_challenges > 0
        ? `${p.name} has ${p.open_challenges} open challenge${p.open_challenges === 1 ? '' : 's'} that will be affected. Their profile, invite history, and match history are preserved — only their ladder participation is removed.`
        : `Their profile, invite history, and match history are preserved — only their ladder participation is removed.`,
      confirmLabel: 'Remove from ladder',
      danger: true,
      onConfirm: async () => {
        try {
          await adminAPI.removePlayer(p.player_id);
          toast.success(`${p.name} removed from the ladder`);
          setConfirm(null);
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Remove failed');
        }
      },
    });
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="admin-ladder-manage-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <Link to="/admin/events" data-testid="back-to-events"
                className="text-[12px] text-[#015e3f] inline-flex items-center gap-1.5 hover:underline">
            <ArrowLeft size={12} weight="bold" /> Back to Admin · Events
          </Link>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#013523] mt-2" data-testid="ladder-title">
            {ladder?.name || FRIENDLY_NAME[slug] || 'Ladder'}
          </h1>
          <p className="text-[12.5px] text-[#6a766e] mt-1">
            Manage the full ladder order. Manual admin changes bypass normal challenge/match movement.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <>
              <button onClick={discard} disabled={saving} data-testid="btn-discard"
                      className="px-3 py-1.5 rounded-full border border-[#e3dcd1] text-[12px] hover:bg-[#f0ece3]">
                Discard
              </button>
              <button onClick={save} disabled={saving} data-testid="btn-save-order"
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#013523] text-white text-[12px] hover:bg-[#024a30] disabled:opacity-60">
                <FloppyDisk size={14} weight="bold" /> {saving ? 'Saving…' : 'Save order'}
              </button>
            </>
          )}
          <button onClick={() => setAddOpen(true)} data-testid="btn-add-player"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#013523] text-[#013523] text-[12px] hover:bg-[#f0ece3]">
            <Plus size={14} weight="bold" /> Add player
          </button>
        </div>
      </div>

      {dirty && (
        <div className="mb-4 rounded-xl border border-[#c9a93a]/60 bg-[#fffbf0] px-4 py-3 text-[12.5px] text-[#5b4b16]" data-testid="dirty-banner">
          <div className="flex items-center gap-2 font-medium">
            <Warning size={14} weight="fill" className="text-[#c9a93a]" /> Unsaved changes
          </div>
          <p className="mt-1 text-[#6a766e]">
            {movedFrom.filter((p) => p.moved).length} player{movedFrom.filter((p) => p.moved).length === 1 ? '' : 's'} will be repositioned. Ranks re-seed contiguously 1..N on save.
          </p>
        </div>
      )}

      {ladder?.id && (
        <div className="mb-4">
          <EventMediaCard
            kind="ladder"
            eventId={ladder.id}
            eventName={ladder.name}
            flyerUrl={ladder.flyer_url}
            featuredOnHome={ladder.featured_on_home}
            onChange={load}
          />
        </div>
      )}

      {loading ? (
        <div className="text-[13px] text-[#6a766e] py-16 text-center">Loading roster…</div>
      ) : movedFrom.length === 0 ? (
        <div className="rounded-xl border border-[#e3dcd1] bg-white p-10 text-center" data-testid="empty-ladder">
          <p className="text-[14px] text-[#013523] font-medium">No players in this ladder yet</p>
          <p className="text-[12.5px] text-[#6a766e] mt-1">Add a player to get started.</p>
          <button onClick={() => setAddOpen(true)}
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#013523] text-white text-[12px] hover:bg-[#024a30]">
            <Plus size={14} weight="bold" /> Add first player
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#e3dcd1] rounded-xl overflow-hidden">
          <div className="grid grid-cols-[64px_1fr_100px_120px_120px] items-center gap-3 px-4 py-2.5 border-b border-[#e3dcd1] bg-[#f8f6f0] text-[10.5px] uppercase tracking-widest text-[#6a766e]">
            <div>Rank</div>
            <div>Player</div>
            <div className="text-center">Record</div>
            <div className="text-center">Challenges</div>
            <div className="text-right">Actions</div>
          </div>
          <ol className="divide-y divide-[#f0ece3]">
            {movedFrom.map((p, idx) => (
              <li key={p.player_id}
                  data-testid={`ladder-row-${p.player_id}`}
                  draggable
                  onDragStart={() => onDragStart(p.player_id)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(idx)}
                  className={`grid grid-cols-[64px_1fr_100px_120px_120px] items-center gap-3 px-4 py-2.5
                    ${p.moved ? 'bg-[#fffbf0]' : ''}
                    ${dragId === p.player_id ? 'opacity-40' : ''}
                    hover:bg-[#fafaf7]`}>
                <div className="flex items-center gap-1.5">
                  <DotsSixVertical size={14} weight="bold" className="text-[#6a766e] cursor-grab" />
                  <input type="number" min={1} max={movedFrom.length} value={idx + 1}
                         onChange={(e) => editRank(idx, e.target.value)}
                         data-testid={`rank-input-${p.player_id}`}
                         className="w-11 text-[13px] tabular-nums text-center border border-[#e3dcd1] rounded px-1 py-0.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13.5px] text-[#013523] font-medium truncate" data-testid={`row-name-${p.player_id}`}>
                    {p.name}
                    {p.moved && p.old_rank && (
                      <span className="ml-2 text-[10.5px] text-[#c9a93a] font-normal">
                        (was #{p.old_rank})
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#6a766e] mt-0.5">
                    {p.status !== 'active' && <span className="mr-2 text-[#9c2626]">{p.status}</span>}
                    {p.profile_id && (
                      <Link to={`/player/${p.player_id}`} className="text-[#015e3f] hover:underline">Open profile</Link>
                    )}
                  </div>
                </div>
                <div className="text-center text-[12px] text-[#6a766e] tabular-nums">
                  {p.wins}<span className="opacity-40"> · </span>{p.losses}
                </div>
                <div className="text-center text-[12px]">
                  {p.open_challenges > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[#9c2626]" data-testid={`open-ch-${p.player_id}`}>
                      <Warning size={12} weight="fill" /> {p.open_challenges} open
                    </span>
                  ) : (
                    <span className="text-[#6a766e]">—</span>
                  )}
                </div>
                <div className="flex items-center justify-end gap-1">
                  <IconBtn onClick={() => moveTop(idx)}    disabled={idx === 0}                     icon={<ArrowLineUp size={12} weight="bold" />}   title="Move to top"    testid={`top-${p.player_id}`} />
                  <IconBtn onClick={() => move(idx, -1)}   disabled={idx === 0}                     icon={<ArrowUp size={12} weight="bold" />}       title="Move up"        testid={`up-${p.player_id}`} />
                  <IconBtn onClick={() => move(idx, +1)}   disabled={idx === movedFrom.length - 1}  icon={<ArrowDown size={12} weight="bold" />}     title="Move down"      testid={`down-${p.player_id}`} />
                  <IconBtn onClick={() => moveBottom(idx)} disabled={idx === movedFrom.length - 1}  icon={<ArrowLineDown size={12} weight="bold" />} title="Move to bottom" testid={`bottom-${p.player_id}`} />
                  <IconBtn onClick={() => removePlayer(p)} icon={<Trash size={12} weight="bold" />} title="Remove from ladder" testid={`remove-${p.player_id}`} danger />
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {addOpen && (
        <AddPlayerDialog slug={slug} ladderName={ladder?.name || 'ladder'} rosterSize={players.length}
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); load(); }} />
      )}

      {confirm && (
        <ConfirmDialog
          open
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          destructive={confirm.danger}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function IconBtn({ onClick, disabled, icon, title, testid, danger }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} data-testid={testid}
            className={`w-7 h-7 flex items-center justify-center rounded-md border transition-colors
              ${danger ? 'border-[#f0b7b7] text-[#9c2626] hover:bg-[#fde7e7]' : 'border-[#e3dcd1] text-[#013523] hover:bg-[#f0ece3]'}
              disabled:opacity-30 disabled:cursor-not-allowed`}>
      {icon}
    </button>
  );
}

// ---------------- Add-player dialog ----------------
function AddPlayerDialog({ slug, ladderName, rosterSize, onClose, onAdded }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState('');
  const [placement, setPlacement] = useState('bottom'); // 'top' | 'bottom' | 'custom'
  const [customRank, setCustomRank] = useState(rosterSize + 1);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminAPI.getProfilesWithoutPlayer(slug);
        setCandidates(r.data?.profiles || []);
      } catch (e) {
        toast.error('Failed to load eligible members');
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const filtered = candidates.filter((c) => {
    if (!search.trim()) return true;
    const t = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(t) || (c.email || '').toLowerCase().includes(t);
  });

  const add = async (c) => {
    setBusyId(c.profile_id);
    try {
      const r = await adminAPI.addPlayerToLadder(c.profile_id, slug);
      // Adjust rank if not bottom
      let targetRank = null;
      if (placement === 'top') targetRank = 1;
      else if (placement === 'custom') targetRank = Math.max(1, Math.min(rosterSize + 1, parseInt(customRank, 10) || rosterSize + 1));
      if (targetRank) {
        // The add-to-ladder endpoint returns the new player_id; move it into place.
        const newPlayerId = r.data?.player_id || r.data?.id;
        if (newPlayerId && targetRank !== (rosterSize + 1)) {
          await adminAPI.adjustRank(newPlayerId, targetRank);
        }
      }
      toast.success(`${c.name} added to ${ladderName}`);
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    } finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}
         data-testid="add-player-dialog">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e3dcd1] flex items-center justify-between">
          <h2 className="font-serif text-lg text-[#013523]">Add player to {ladderName}</h2>
          <button onClick={onClose} data-testid="add-player-close"
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f0ece3]">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#6a766e]">Initial placement</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[
                { v: 'top',    l: 'Top (#1)' },
                { v: 'bottom', l: `Bottom (#${rosterSize + 1})` },
                { v: 'custom', l: 'Custom rank' },
              ].map((o) => (
                <button key={o.v}
                  onClick={() => setPlacement(o.v)}
                  data-testid={`placement-${o.v}`}
                  className={`text-[12px] px-3 py-2 rounded-full border transition-colors
                    ${placement === o.v ? 'bg-[#013523] text-white border-[#013523]' : 'bg-white text-[#013523] border-[#e3dcd1] hover:bg-[#f0ece3]'}`}>
                  {o.l}
                </button>
              ))}
            </div>
            {placement === 'custom' && (
              <input type="number" min={1} max={rosterSize + 1} value={customRank}
                     onChange={(e) => setCustomRank(e.target.value)}
                     data-testid="placement-custom-rank"
                     className="mt-2 w-24 text-[13px] border border-[#e3dcd1] rounded px-2 py-1" />
            )}
          </div>
          <div>
            <div className="relative">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search eligible members"
                     data-testid="add-player-search"
                     className="w-full pl-9 pr-3 py-2 text-[13px] border border-[#e3dcd1] rounded-lg" />
            </div>
            <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-[#e3dcd1] divide-y divide-[#f0ece3]" data-testid="add-player-list">
              {loading ? (
                <div className="text-[12.5px] text-[#6a766e] p-4 text-center">Loading eligible members…</div>
              ) : filtered.length === 0 ? (
                <div className="text-[12.5px] text-[#6a766e] p-4 text-center">
                  {candidates.length === 0 ? 'No eligible members — every approved member is already in this ladder.' : 'No matches.'}
                </div>
              ) : filtered.map((c) => (
                <div key={c.profile_id} className="flex items-center justify-between px-3 py-2"
                     data-testid={`add-candidate-${c.profile_id}`}>
                  <div className="min-w-0">
                    <div className="text-[13px] text-[#013523] truncate">{c.name}</div>
                    <div className="text-[11px] text-[#6a766e] truncate">{c.email}</div>
                  </div>
                  <button onClick={() => add(c)} disabled={busyId === c.profile_id}
                          data-testid={`add-btn-${c.profile_id}`}
                          className="inline-flex items-center gap-1 text-[11.5px] px-2.5 py-1 rounded-full bg-[#013523] text-white hover:bg-[#024a30] disabled:opacity-60">
                    <CheckCircle size={12} weight="fill" /> {busyId === c.profile_id ? 'Adding…' : 'Add'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
